<!-- Rendered in the app header's centre slot (HeaderContentService), not in
     this page's own layout. -->
<ng-template #headerMenu>
    <div class="header-page-title">พัสดุคืนสินค้า</div>
</ng-template>

<div class="dx-card padding-content">
    <div class="toolbar">
        <div class="scan-field">
            <dx-text-box
                #searchBox
                label="สแกน / พิมพ์เลขคำสั่งซื้อ เลขพัสดุ หรือเลขคำขอคืน"
                labelMode="floating"
                [value]="searchCode"
                valueChangeEvent="input"
                [showClearButton]="true"
                [width]="360"
                (onValueChanged)="onSearchChanged($event)"
                (onEnterKey)="onSearchEnter()"
                (onKeyDown)="onSearchKeyDown($event)"
            >
                <!-- Declaring buttons replaces the built-in ones, so the
                     clear button is listed explicitly. -->
                <dxi-text-box-button
                    name="scan"
                    location="before"
                    [options]="scanIconButton"
                ></dxi-text-box-button>
                <dxi-text-box-button
                    name="clear"
                    location="after"
                ></dxi-text-box-button>
            </dx-text-box>
            <!-- Never disabled while loading: a scan made then would be lost.
                 Enter during a load waits for it in the result popup. -->
            @if (hasSearch) {
                <span class="match-count">
                    @if (awaitingLoad) {
                        ยังโหลดไม่ครบ · กด Enter เพื่อรอผล
                    } @else if (matches.length === 0) {
                        ไม่พบรายการ · กด Enter เพื่อตรวจสอบ
                    } @else {
                        พบ {{ matches.length }} รายการ · กด Enter เพื่อดูผล
                    }
                </span>
            }
        </div>

        <!-- Takes whatever width the scan box and the filters leave, and
             clips with an ellipsis rather than wrapping the filters onto a
             second line; the full text is in the hover title. -->
        <div class="summary" [title]="summaryText">
            @if (loading && reports.length < storeCount) {
                <span class="loading-note"
                    ><dx-load-indicator
                        class="inline-spinner"
                        [height]="16"
                        [width]="16"
                    ></dx-load-indicator>
                    กำลังโหลด {{ reports.length }}/{{ storeCount }} ร้าน ·</span
                >
            } @else if (loading || refreshing) {
                <span class="loading-note"
                    ><dx-load-indicator
                        class="inline-spinner"
                        [height]="16"
                        [width]="16"
                    ></dx-load-indicator>
                    กำลังอัปเดตพัสดุตีกลับล่าสุด ·</span
                >
            }
            <!-- How old the collected data is: an icon whose hover says so,
                 since the line has no room to spare for it. -->
            @if (indexedAt) {
                <i
                    class="dx-icon-clock data-age"
                    [title]="
                        'ข้อมูล ' +
                        (indexedAt | thaiDateOrRelative: false) +
                        ' (' +
                        (indexedAt | thaiDateOrRelative: true : false) +
                        ') — รวบรวมจากทุกร้านทุก 15 นาที (08:00–21:00)'
                    "
                ></i>
            }
            {{ fetchedCount }} พัสดุคืน/ตีกลับใน {{ lookbackDays }} วัน ·
            <b>{{ unreceivedExpectedCount }}</b> ยังไม่ยืนยันรับ
            @if (!receiptsLoaded) {
                ·
                <span class="warn"
                    >ยังโหลดข้อมูลการรับไม่ได้ — ดูได้อย่างเดียว</span
                >
            }
            @for (report of failedReports; track report.storeId) {
                ·
                <span class="warn">{{ report.title }}: {{ report.error }}</span>
            }
            @for (report of failedDeliveryReports; track report.storeId) {
                ·
                <span class="warn"
                    >{{ report.title }}: โหลดพัสดุตีกลับไม่ได้</span
                >
            }
            @if (detailFailureCount > 0) {
                ·
                <span class="warn"
                    >{{ detailFailureCount }} รายการ Shopee
                    ไม่มีสถานะขนส่ง</span
                >
            }
        </div>

        <div class="toolbar-right">
            <dx-check-box
                [(value)]="hideReceived"
                [disabled]="hasSearch"
                text="ซ่อนรายการที่ยืนยันรับแล้ว"
                hint="ซ่อนรายการที่ยืนยันรับแล้ว (การค้นหาจะแสดงทุกรายการ)"
                (onValueChanged)="onFilterChanged()"
            ></dx-check-box>
            <dx-number-box
                [disabled]="loading"
                [showSpinButtons]="false"
                format="#,##0 วัน"
                [min]="7"
                [max]="365"
                label="แสดงคำขอคืนสินค้าภายใน"
                labelMode="floating"
                [(value)]="lookbackDays"
                [width]="210"
                (onEnterKey)="load()"
            ></dx-number-box>
            <dx-button
                [disabled]="loading"
                stylingMode="contained"
                icon="refresh"
                hint="ดึงข้อมูลคืนสินค้าจากทุกร้านใหม่"
                (onClick)="load()"
            ></dx-button>
            <a
                class="text-link"
                title="ตรวจสอบสินค้าจากพัสดุที่รับแล้ว — เข้าสต็อกหรือตัดจำหน่าย"
                (click)="openInspection()"
                >จัดการสินค้าคืนเข้าสต็อก →</a
            >
        </div>
    </div>

    <dx-data-grid
        #dataGrid
        id="returnPackagesGrid"
        height="100%"
        [dataSource]="dataSource"
        keyExpr="id"
        [showBorders]="true"
        [columnAutoWidth]="false"
        [allowColumnResizing]="true"
        [showRowLines]="true"
        [rowAlternationEnabled]="true"
        [hoverStateEnabled]="true"
        [wordWrapEnabled]="false"
        [noDataText]="
            loading
                ? 'กำลังโหลด…'
                : hasSearch
                  ? 'ไม่พบรายการคืนที่ตรงกับรหัสนี้'
                  : 'ไม่มีพัสดุคืนที่จะแสดง'
        "
        (onRowPrepared)="onRowPrepared($event)"
    >
        <dxo-paging [pageSize]="50"></dxo-paging>
        <dxo-pager
            [showPageSizeSelector]="true"
            [allowedPageSizes]="[25, 50, 100]"
            [showInfo]="true"
        ></dxo-pager>
        <dxo-filter-row [visible]="true"></dxo-filter-row>
        <dxo-header-filter [visible]="true"></dxo-header-filter>
        <dxo-master-detail
            [enabled]="true"
            template="detailTemplate"
        ></dxo-master-detail>

        <dxi-column
            dataField="storeTitle"
            caption="ร้านค้า"
            [width]="140"
        ></dxi-column>
        <!-- A return: when it was requested. A failed delivery: when the
             courier turned it around (Lazada, Shopee) or it got back
             (TikTok) — the platforms record nothing earlier. -->
        <dxi-column
            dataField="createdAtDate"
            caption="วันที่"
            dataType="date"
            [width]="140"
            sortOrder="desc"
            cellTemplate="relativeDatetimeCellTemplate"
        ></dxi-column>
        <dxi-column
            dataField="sourceLabel"
            caption="ประเภท"
            [width]="110"
            alignment="center"
            cellTemplate="sourceCellTemplate"
        ></dxi-column>
        <dxi-column
            dataField="orderNo"
            caption="เลขคำสั่งซื้อ"
            [width]="170"
        ></dxi-column>
        <!-- A return's own label, or — for a failed delivery — the label
             it went out on. -->
        <dxi-column
            dataField="trackingNumber"
            caption="เลขพัสดุ"
            [width]="160"
        ></dxi-column>
        <dxi-column
            dataField="carrier"
            caption="ขนส่ง"
            [width]="150"
            cellTemplate="carrierCellTemplate"
        ></dxi-column>
        <dxi-column
            dataField="stageLabel"
            caption="พัสดุ"
            [width]="140"
            cellTemplate="stageCellTemplate"
        ></dxi-column>
        <dxi-column
            dataField="platformStatus"
            caption="สถานะแพลตฟอร์ม"
            [width]="180"
        ></dxi-column>
        <dxi-column
            dataField="itemsSummary"
            caption="สินค้า"
            [minWidth]="140"
            [allowHeaderFiltering]="false"
            cellTemplate="itemsCellTemplate"
        ></dxi-column>
        <dxi-column
            dataField="refundAmount"
            caption="เงินคืน"
            dataType="number"
            [width]="90"
            [allowHeaderFiltering]="false"
            cellTemplate="priceTemplate"
        ></dxi-column>
        <dxi-column
            dataField="returnType"
            caption="ประเภทคำขอ"
            [width]="150"
            [visible]="false"
        ></dxi-column>
        <dxi-column
            dataField="returnId"
            caption="เลขคำขอคืน"
            [width]="170"
            [visible]="false"
        ></dxi-column>
        <dxi-column
            dataField="reason"
            caption="เหตุผล"
            [minWidth]="160"
            [visible]="false"
            [allowHeaderFiltering]="false"
        ></dxi-column>
        <dxi-column
            dataField="received"
            caption="รับแล้ว"
            dataType="boolean"
            [width]="180"
            [allowFiltering]="false"
            cellTemplate="receivedCellTemplate"
        ></dxi-column>

        <!-- Click any date to flip every date in the table between relative
             ("3 วันที่แล้ว") and the real date; the other form stays in the
             hover. -->
        <div *dxTemplate="let cellData of 'relativeDatetimeCellTemplate'">
            <span
                class="date-toggle"
                [title]="
                    (cellData.value
                        | thaiDateOrRelative: !showAbsoluteDates : false) +
                    dateToggleHint
                "
                (click)="toggleDateMode($event)"
                >{{
                    cellData.value | thaiDateOrRelative: showAbsoluteDates
                }}</span
            >
        </div>
        <!-- The type opens the detail popup. -->
        <div
            *dxTemplate="let cellData of 'sourceCellTemplate'"
            class="source-cell"
        >
            <span
                class="source source-button source-{{ cellData.data.source }}"
                title="ดูรายละเอียด"
                (click)="openDetail(cellData.data, $event)"
                >{{ cellData.value }}</span
            >
        </div>
        <div *dxTemplate="let cellData of 'priceTemplate'">
            @if (cellData.value !== null) {
                {{ cellData.value | formatNumber: 2 }}
            }
        </div>
        <!-- The template's own div replaces the cell text DevExtreme would
             otherwise ellipsize, so it has to clip itself. -->
        <div
            *dxTemplate="let cellData of 'carrierCellTemplate'"
            class="cell-ellipsis"
            [class.carrier-inferred]="cellData.data.carrierInferred"
            [title]="
                cellData.data.carrierInferred
                    ? cellData.value +
                      ' — แพลตฟอร์มไม่ระบุขนส่ง อนุมานจากเลขพัสดุ'
                    : cellData.value
            "
        >
            {{ cellData.value }}
        </div>
        <div *dxTemplate="let cellData of 'stageCellTemplate'">
            <span
                class="stage stage-{{
                    cellData.data.received
                        ? 'received'
                        : cellData.data.packageStage
                }}"
                [title]="
                    (cellData.data.received
                        ? 'สถานะแพลตฟอร์ม: ' +
                          cellData.data.platformStageLabel +
                          ' — '
                        : '') +
                    (cellData.data.logisticsStatus ||
                        cellData.data.platformStatus)
                "
            >
                {{ cellData.value }}
            </span>
        </div>
        <div
            *dxTemplate="let cellData of 'receivedCellTemplate'"
            class="received-cell"
        >
            @if (cellData.data.received) {
                <span
                    class="received-mark date-toggle"
                    [title]="
                        (cellData.data.receivedAt
                            | thaiDateOrRelative: !showAbsoluteDates : false) +
                        (cellData.data.receivedBy
                            ? ' · ' + cellData.data.receivedBy
                            : '') +
                        dateToggleHint
                    "
                    (click)="toggleDateMode($event)"
                >
                    <i class="dx-icon-check"></i>
                    {{
                        cellData.data.receivedAt
                            | thaiDateOrRelative: showAbsoluteDates
                    }}
                </span>
                <dx-button
                    stylingMode="text"
                    icon="revert"
                    hint="ยกเลิกการยืนยันรับ"
                    [disabled]="receiptSaving || !receiptsLoaded"
                    (onClick)="unmarkReceived(cellData.data)"
                ></dx-button>
            } @else {
                <dx-button
                    stylingMode="outlined"
                    type="success"
                    icon="check"
                    text="ยืนยันได้รับแล้ว"
                    hint="ยืนยันว่าพัสดุคืนนี้มาถึงร้านแล้ว"
                    [disabled]="receiptSaving || !receiptsLoaded"
                    (onClick)="confirmReceived(cellData.data)"
                ></dx-button>
            }
        </div>
        <div
            *dxTemplate="let cellData of 'itemsCellTemplate'"
            class="cell-ellipsis"
            [title]="cellData.value"
        >
            @for (item of cellData.data.items; track $index) {
                @if (!$first) {
                    ,
                }
                <a
                    class="sku-link"
                    (click)="openProduct(item.sku); $event.stopPropagation()"
                    >{{ item.sku }}</a
                >
                @if (item.qty > 1) {
                    ×{{ item.qty }}
                }
            }
        </div>
        <div *dxTemplate="let detail of 'detailTemplate'" class="detail">
            <table class="detail-items">
                <thead>
                    <tr>
                        <th>SKU</th>
                        <th>สินค้า</th>
                        <th class="num">จำนวน</th>
                    </tr>
                </thead>
                <tbody>
                    @for (item of detail.data.items; track $index) {
                        <tr>
                            <td class="sku">
                                <a
                                    class="sku-link"
                                    (click)="openProduct(item.sku)"
                                    >{{ item.sku }}</a
                                >
                            </td>
                            <td>{{ item.name }}</td>
                            <td class="num">{{ item.qty }}</td>
                        </tr>
                    } @empty {
                        <tr>
                            <td colspan="3" class="muted">ไม่มีรายการสินค้า</td>
                        </tr>
                    }
                </tbody>
            </table>
            <div class="detail-meta">
                <span><b>ประเภท</b> {{ detail.data.sourceLabel }}</span>
                @if (detail.data.returnId) {
                    <span><b>เลขคำขอคืน</b> {{ detail.data.returnId }}</span>
                }
                <span><b>ประเภทคำขอ</b> {{ detail.data.returnType }}</span>
                @if (detail.data.carrier) {
                    <span><b>ขนส่ง</b> {{ detail.data.carrier }}</span>
                }
                @if (detail.data.logisticsStatus) {
                    <span
                        ><b>สถานะขนส่ง</b>
                        {{ detail.data.logisticsStatus }}</span
                    >
                }
                @if (detail.data.reason) {
                    <span><b>เหตุผล</b> {{ detail.data.reason }}</span>
                }
                @if (detail.data.updatedAt) {
                    <span
                        ><b>อัปเดต</b>
                        {{
                            detail.data.updatedAt | formatRelativeDatetime: "th"
                        }}</span
                    >
                }
            </div>
            <details>
                <summary>ข้อมูลจากแพลตฟอร์ม</summary>
                <pre>{{ detail.data.raw | json }}</pre>
            </details>
        </div>
    </dx-data-grid>
</div>

<app-product-card-popup #productCard></app-product-card-popup>

<app-return-detail-popup
    #detailPopup
    (skuClick)="openProduct($event)"
    (hidden)="onDetailHidden()"
></app-return-detail-popup>

<!-- What a scan found. Opens on the scanner's own Enter, which never records
     anything; a button, or a further Enter once the result has been read
     (see onOpenResultEnter), takes the action. ปิด / ยกเลิก sits on the far
     left. A found parcel must be answered — ยืนยันได้รับแล้ว or ยกเลิก — so
     nothing else closes it (see awaitingDecision). -->
<dx-popup
    [(visible)]="scanPopupVisible"
    [showTitle]="true"
    [title]="'ผลการสแกน: ' + scanShownCode"
    [dragEnabled]="false"
    [hideOnOutsideClick]="!receiptSaving && !awaitingDecision"
    [showCloseButton]="!awaitingDecision"
    [width]="640"
    height="auto"
    [focusStateEnabled]="false"
    (onHidden)="onScanPopupHidden()"
>
    @if (scanResult; as result) {
        <div class="scan-result">
            @switch (result.kind) {
                @case ("match") {
                    <div class="scan-heading ok">
                        <i class="dx-icon-box"></i> พบพัสดุ{{
                            result.row.sourceLabel
                        }}
                        1 รายการ
                    </div>
                    <ng-container
                        *ngTemplateOutlet="
                            returnSummary;
                            context: { $implicit: result.row }
                        "
                    ></ng-container>
                    <ng-container
                        *ngTemplateOutlet="decisionNudgeHint"
                    ></ng-container>
                    <div class="scan-actions">
                        <dx-button
                            class="close-button"
                            stylingMode="outlined"
                            text="ยกเลิก"
                            hint="ไม่บันทึกรับพัสดุนี้"
                            [disabled]="receiptSaving"
                            (onClick)="cancelScan()"
                        ></dx-button>
                        <dx-button
                            stylingMode="contained"
                            type="success"
                            icon="check"
                            text="ยืนยันได้รับแล้ว (Enter)"
                            hint="บันทึกว่าร้านได้รับพัสดุคืนนี้แล้ว — หรือกด Enter"
                            [disabled]="receiptSaving || !receiptsLoaded"
                            (onClick)="confirmReceived(result.row)"
                        ></dx-button>
                    </div>
                }
                @case ("received") {
                    <div class="scan-heading ok">
                        <i class="dx-icon-check"></i> พัสดุนี้ยืนยันรับไปแล้ว
                        {{
                            result.row.receivedAt | formatRelativeDatetime: "th"
                        }}
                    </div>
                    <ng-container
                        *ngTemplateOutlet="
                            returnSummary;
                            context: { $implicit: result.row }
                        "
                    ></ng-container>
                    <div class="scan-actions">
                        <dx-button
                            class="close-button"
                            stylingMode="contained"
                            text="ปิด (Enter)"
                            (onClick)="scanPopupVisible = false"
                        ></dx-button>
                    </div>
                }
                @case ("multi") {
                    <div class="scan-heading">
                        <i class="dx-icon-search"></i> ตรงกัน
                        {{ result.rows.length }} รายการ — เลือกรายการที่รับ
                    </div>
                    <div class="scan-list">
                        @for (row of result.rows; track row.id) {
                            <div class="scan-list-row">
                                <div class="scan-list-text">
                                    <b>{{ row.storeTitle }}</b> ·
                                    <span
                                        class="source source-{{ row.source }}"
                                        >{{ row.sourceLabel }}</span
                                    >
                                    · {{ row.orderNo }}
                                    @if (row.trackingNumber) {
                                        · {{ row.trackingNumber }}
                                    }
                                    · {{ row.itemsSummary }} ·
                                    <span
                                        class="stage stage-{{
                                            row.received
                                                ? 'received'
                                                : row.packageStage
                                        }}"
                                        >{{ row.stageLabel }}</span
                                    >
                                </div>
                                @if (!row.received) {
                                    <dx-button
                                        stylingMode="outlined"
                                        type="success"
                                        icon="check"
                                        text="ยืนยันได้รับแล้ว"
                                        [disabled]="
                                            receiptSaving || !receiptsLoaded
                                        "
                                        (onClick)="confirmReceived(row)"
                                    ></dx-button>
                                }
                            </div>
                        }
                    </div>
                    <ng-container
                        *ngTemplateOutlet="decisionNudgeHint"
                    ></ng-container>
                    <div class="scan-actions">
                        <dx-button
                            class="close-button"
                            stylingMode="outlined"
                            [text]="awaitingDecision ? 'ยกเลิก' : 'ปิด'"
                            [hint]="
                                awaitingDecision
                                    ? 'ไม่บันทึกรับรายการใดเลย'
                                    : 'ปิด'
                            "
                            [disabled]="receiptSaving"
                            (onClick)="cancelScan()"
                        ></dx-button>
                    </div>
                }
                @case ("pending") {
                    <div class="scan-heading">
                        <dx-load-indicator
                            [height]="24"
                            [width]="24"
                        ></dx-load-indicator>
                        @if (result.stage === "loading") {
                            กำลังโหลดรายการคืนจากทุกร้าน… ({{
                                reports.length
                            }}/{{ storeCount }})
                        } @else if (result.stage === "refresh") {
                            ไม่พบในรายการ — กำลังดึงพัสดุคืน/ตีกลับล่าสุด…
                        } @else {
                            ไม่พบในรายการคืน — กำลังตรวจสอบคำสั่งซื้อที่ยกเลิก…
                        }
                    </div>
                    <div class="scan-hint">
                        @if (result.stage === "loading") {
                            จะแสดงผลทันทีที่พบ หรือเมื่อโหลดครบทุกร้าน
                        } @else if (result.stage === "refresh") {
                            พัสดุตีกลับบางแพลตฟอร์มขึ้นในระบบตอนถึงร้านพอดี —
                            ขอดึงข้อมูลล่าสุดก่อน
                        } @else {
                            ครั้งแรกใช้เวลาสักครู่
                            (ดึงคำสั่งซื้อที่ยกเลิกจากทุกร้าน)
                        }
                    </div>
                }
                @case ("cancelled") {
                    <div class="scan-heading cancelled">
                        <i class="dx-icon-warning"></i>
                        พัสดุยกเลิกก่อนส่งมอบให้ขนส่ง
                    </div>
                    <div class="scan-meta">
                        <span
                            ><b>ร้านค้า</b> {{ result.order.storeTitle }}</span
                        >
                        <span
                            ><b>คำสั่งซื้อ</b> {{ result.order.orderNo }}</span
                        >
                        @if (result.order.trackingNumbers.length) {
                            <span
                                ><b>เลขพัสดุ</b>
                                {{
                                    result.order.trackingNumbers.join(", ")
                                }}</span
                            >
                        }
                    </div>
                    <div class="scan-hint">
                        ไม่ต้องบันทึกรับคืน —
                        การยกเลิกคำสั่งซื้อคืนสินค้าเข้าสต็อกโดยอัตโนมัติแล้ว
                    </div>
                    <div class="scan-actions">
                        <dx-button
                            stylingMode="contained"
                            text="รับทราบ (Enter)"
                            (onClick)="scanPopupVisible = false"
                        ></dx-button>
                    </div>
                }
                @case ("order") {
                    <div class="scan-heading">
                        <i class="dx-icon-info"></i>
                        @if (result.order.failedDelivery) {
                            คำสั่งซื้อนี้เป็นพัสดุตีกลับ แต่ไม่อยู่ในตาราง
                        } @else {
                            พบคำสั่งซื้อ แต่ไม่ได้ยกเลิกและไม่มีคำขอคืน
                        }
                    </div>
                    <div class="scan-meta">
                        <span
                            ><b>ร้านค้า</b> {{ result.order.storeTitle }}</span
                        >
                        <span
                            ><b>คำสั่งซื้อ</b> {{ result.order.orderNo }}</span
                        >
                        <span><b>สถานะ</b> {{ result.order.status }}</span>
                    </div>
                    @if (result.failed.length > 0) {
                        <div class="scan-hint warn">
                            ตรวจสอบ {{ result.failed.join(", ") }} ไม่ได้
                        </div>
                    }
                    <div class="scan-hint">
                        @if (result.order.failedDelivery) {
                            อาจเก่ากว่า {{ lookbackDays }} วันที่แสดง —
                            เพิ่มจำนวนวันแล้วโหลดใหม่ หรือบันทึกไว้ก่อน
                        } @else {
                            อาจเป็นพัสดุตีกลับที่แพลตฟอร์มยังไม่อัปเดต —
                            ถ้าพัสดุอยู่ที่ร้านจริง บันทึกไว้ก่อนได้
                        }
                    </div>
                    <div class="scan-actions">
                        <dx-button
                            class="close-button"
                            stylingMode="outlined"
                            text="ปิด"
                            [disabled]="receiptSaving"
                            (onClick)="scanPopupVisible = false"
                        ></dx-button>
                        <dx-button
                            stylingMode="contained"
                            type="default"
                            icon="plus"
                            text="บันทึกไว้ก่อน"
                            [disabled]="receiptSaving || !receiptsLoaded"
                            (onClick)="recordUnmatched()"
                        ></dx-button>
                    </div>
                }
                @case ("none") {
                    <div class="scan-heading">
                        <i class="dx-icon-help"></i>
                        ไม่พบทั้งในรายการคืนและคำสั่งซื้อที่ยกเลิก
                    </div>
                    @if (result.failed.length > 0) {
                        <div class="scan-hint warn">
                            ตรวจสอบคำสั่งซื้อที่ยกเลิกของ
                            {{ result.failed.join(", ") }} ไม่ได้ —
                            อาจเป็นพัสดุยกเลิกของร้านนี้
                        </div>
                    }
                    <!-- What to try before recording it. Step 2 is the usual
                         answer when the code reads like a courier label: a
                         courier sends an undeliverable parcel back under a
                         return label of its own, which no platform records. -->
                    <div class="scan-steps-title">ลองตามลำดับนี้</div>
                    <ol class="scan-steps">
                        <li>ตรวจสอบชื่อว่าเป็นพัสดุของร้านหรือไม่</li>
                        <li [class.highlight]="scannedCourierLabel">
                            ลองสแกนเลขคำสั่งซื้อจากใบปะหน้าเดิมที่ถูกแปะทับ
                            @if (scannedCourierLabel) {
                                <div class="scan-hint">
                                    เลขที่สแกนน่าจะเป็นใบคืนของขนส่ง (เช่น
                                    SPXTH…, LEXPU…)
                                    ซึ่งไม่มีในข้อมูลของแพลตฟอร์ม
                                </div>
                            }
                        </li>
                        <li>
                            พิมพ์เลขคำสั่งซื้อจากใบกำกับสินค้าที่แนบไปกับสินค้า
                        </li>
                    </ol>
                    <div class="scan-hint">
                        ถ้ายังไม่พบ และพัสดุเป็นของร้านจริง บันทึกไว้ก่อนได้ —
                        จะไปแสดงที่รายการคืนเมื่อแพลตฟอร์มส่งข้อมูลมา
                    </div>
                    <div class="scan-actions">
                        <dx-button
                            class="close-button"
                            stylingMode="outlined"
                            text="ปิด"
                            [disabled]="receiptSaving"
                            (onClick)="scanPopupVisible = false"
                        ></dx-button>
                        <dx-button
                            stylingMode="contained"
                            type="default"
                            icon="plus"
                            text="บันทึกไว้ก่อน"
                            [disabled]="receiptSaving || !receiptsLoaded"
                            (onClick)="recordUnmatched()"
                        ></dx-button>
                    </div>
                }
            }
        </div>
    }
</dx-popup>

<!-- Shown when the operator tried to leave a found parcel unanswered. -->
<ng-template #decisionNudgeHint>
    @if (decisionNudge) {
        <div class="scan-hint warn">
            ยังไม่ได้บันทึก — กด ยืนยันได้รับแล้ว หรือ ยกเลิก
            @if (queuedEnter) {
                · พัสดุที่สแกนต่อจะแสดงผลหลังจากนี้
            }
        </div>
    }
</ng-template>

<ng-template #returnSummary let-row>
    <div class="scan-meta">
        <span><b>ร้านค้า</b> {{ row.storeTitle }}</span>
        <span
            ><b>ประเภท</b>
            <span class="source source-{{ row.source }}">{{
                row.sourceLabel
            }}</span></span
        >
        <span><b>คำสั่งซื้อ</b> {{ row.orderNo }}</span>
        @if (row.trackingNumber) {
            <span><b>เลขพัสดุ</b> {{ row.trackingNumber }}</span>
        }
        @if (row.carrier) {
            <span><b>ขนส่ง</b> {{ row.carrier }}</span>
        }
        <span
            ><b>พัสดุ</b>
            <span
                class="stage stage-{{
                    row.received ? 'received' : row.packageStage
                }}"
                >{{ row.stageLabel }}</span
            ></span
        >
        <span><b>สินค้า</b> {{ row.itemsSummary }}</span>
        @if (row.reason) {
            <span><b>เหตุผล</b> {{ row.reason }}</span>
        }
    </div>
</ng-template>
